import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { IDropdown, IItinerary } from "../../models/BaseModel";
import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";

interface Iprops {
    singleData: IItinerary;
    destination: string;
    packageTitle: string;
    packageDuration: number;
    cityId: number;
    open: boolean;
    hideDialog: () => void;
    toast: any;
    reloadData: () => void;
    hasPermission: (pageName: string, featureName?: string) => boolean;
}

interface IActivityDrop extends IDropdown {
    desc?: string;
}

interface IActivityRow {
    activity: IActivityDrop;
    selected: boolean;
    duration: number;
    day: number;
    hour: number;
    minute: number;
}

export const Form = (props: Iprops) => {
    const [submitting, setSubmitting] = useState(false);
    const [activities, setActivities] = useState<IActivityDrop[]>([]);
    const [rows, setRows] = useState<IActivityRow[]>([]);
    const [dayOptions, setDayOptions] = useState<IDropdown[]>([]);

    const hourOptions = Array.from({ length: 24 }, (_, i) => ({
        id: i,
        name: `${i.toString().padStart(2, "0")}`,
    }));

    const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
        id: i,
        name: `${i.toString().padStart(2, "0")}`,
    }));

    useEffect(() => {
        if (props.packageDuration) {
            const days = props.packageDuration / 24;
            setDayOptions(
                Array.from({ length: days + 1 }, (_, i) => ({
                    id: i + 1,
                    name: `${i + 1}`,
                }))
            );
        }
    }, [props.packageDuration]);

    const loadActivities = async (cityId: number) => {
        const response = await Fetch(`activity/dropdown/${cityId}`, "GET", null, null, null);
        if (response) {
            const data: IActivityDrop[] = await response.json();
            setActivities(data);
            buildRows(data, props.singleData);
        }
    };

    const buildRows = (data: IActivityDrop[], singleData: IItinerary) => {
        setRows(
            data.map((activity) => ({
                activity,
                selected: false,
                duration: 60,
                day: singleData?.day ?? 1,
                hour: singleData?.hour ?? 0,
                minute: singleData?.minute ?? 0,
            }))
        );
    };

    useEffect(() => {
        if (props.cityId) loadActivities(props.cityId);
    }, [props.cityId]);

    // When dialog opens, re-seed day/hour/minute from singleData (Table pre-calculates these)
    useEffect(() => {
        if (props.open && activities.length > 0) {
            buildRows(activities, props.singleData);
        }
        if (!props.open) {
            setRows((prev) =>
                prev.map((r) => ({ ...r, selected: false, duration: 60 }))
            );
        }
    }, [props.open]);

    const toggleRow = (index: number) => {
        setRows((prev) =>
            prev.map((r, i) => (i === index ? { ...r, selected: !r.selected } : r))
        );
    };

    const updateRow = (index: number, field: keyof IActivityRow, value: any) => {
        setRows((prev) =>
            prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
        );
    };

    const getEndTime = (row: IActivityRow): string => {
        const startTime = (row.day - 1) * 24 * 60 + row.hour * 60 + row.minute;
        const endTotal = startTime + row.duration;
        const endDay = Math.floor(endTotal / (24 * 60)) + 1;
        const endHour = Math.floor((endTotal % (24 * 60)) / 60);
        const endMinute = endTotal % 60;
        return `Day ${endDay} | ${endHour.toString().padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`;
    };

    const selectedRows = rows.filter((r) => r.selected);

    const saveToDB = async () => {
        if (selectedRows.length === 0) {
            props.toast.current.show({ severity: "warn", detail: "Please select at least one activity.", life: 3000 });
            return;
        }

        setSubmitting(true);

        const payloads: IItinerary[] = selectedRows.map((r) => ({
            packageId: props.singleData?.packageId,
            checkIn: props.singleData?.checkIn,
            activityId: r.activity.id,
            duration: r.duration,
            startTime: (r.day - 1) * 24 * 60 + r.hour * 60 + r.minute,
            desc: r.activity.desc ?? "",
            isActive: true,
        }));

        try {
            const response = await Fetch("itinerary/bulk", "POST", payloads, null, props.toast);
            if (response) {
                const result = await response.json();
                props.toast.current.show({
                    severity: "success",
                    detail: `${result.added} activit${result.added > 1 ? "ies" : "y"} added successfully`,
                    life: 3000,
                });
                onHide();
                props.reloadData();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const onHide = () => {
        props.hideDialog();
    };

    const dialogHeader = (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span>Add Itinerary Activities</span>
            <small style={{ fontWeight: "normal", color: "#6c757d", fontSize: "0.82rem" }}>
                {props.destination} &nbsp;|&nbsp; {props.packageTitle} &nbsp;|&nbsp; {props.singleData?.checkIn}
            </small>
        </div>
    );

    return (
        <Dialog
            visible={props.open}
            style={{ width: "92vw", maxWidth: "860px" }}
            header={dialogHeader}
            modal
            onHide={onHide}
            contentStyle={{ padding: "0 1.25rem 1rem" }}
        >
            {rows.length === 0 ? (
                <div style={{ textAlign: "center", padding: "2rem", color: "#6c757d" }}>
                    No activities available for this city.
                </div>
            ) : (
                <>
                    {/* Table header */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "36px 1fr 140px 100px 90px 90px 110px",
                            gap: "0.5rem",
                            alignItems: "center",
                            padding: "0.6rem 0.5rem",
                            background: "var(--surface-100, #f8f9fa)",
                            borderRadius: "6px 6px 0 0",
                            borderBottom: "1px solid var(--surface-300, #dee2e6)",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            color: "var(--text-color-secondary, #6c757d)",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <span />
                        <span>Activity</span>
                        <span>Duration (min)</span>
                        <span>Day</span>
                        <span>Hour</span>
                        <span>Minute</span>
                        <span>End Time</span>
                    </div>

                    {/* Scrollable rows */}
                    <div style={{ maxHeight: "55vh", overflowY: "auto", borderBottom: "1px solid var(--surface-300, #dee2e6)" }}>
                        {rows.map((row, index) => (
                            <div
                                key={row.activity.id}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "36px 1fr 140px 100px 90px 90px 110px",
                                    gap: "0.5rem",
                                    alignItems: "center",
                                    padding: "0.55rem 0.5rem",
                                    background: row.selected
                                        ? "var(--highlight-bg, #eff6ff)"
                                        : index % 2 === 0
                                            ? "var(--surface-0, #fff)"
                                            : "var(--surface-50, #fafafa)",
                                    borderBottom: "1px solid var(--surface-200, #eee)",
                                    transition: "background 0.15s",
                                    cursor: "pointer",
                                }}
                                onClick={() => toggleRow(index)}
                            >
                                {/* Checkbox */}
                                <div onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        checked={row.selected}
                                        onChange={() => toggleRow(index)}
                                    />
                                </div>

                                {/* Activity name */}
                                <span
                                    style={{
                                        fontWeight: row.selected ? 600 : 400,
                                        fontSize: "0.9rem",
                                        color: row.selected ? "var(--primary-color, #2563eb)" : "inherit",
                                        userSelect: "none",
                                    }}
                                >
                                    {row.activity.name}
                                </span>

                                {/* Duration */}
                                <div onClick={(e) => e.stopPropagation()}>
                                    <InputNumber
                                        value={row.duration}
                                        onValueChange={(e) => updateRow(index, "duration", e.value ?? 1)}
                                        min={1}
                                        disabled={!row.selected}
                                        inputStyle={{ width: "100%", padding: "0.3rem 0.5rem", fontSize: "0.85rem" }}
                                        style={{ width: "100%" }}
                                    />
                                </div>

                                {/* Day */}
                                <div onClick={(e) => e.stopPropagation()}>
                                    <Dropdown
                                        value={row.day}
                                        options={dayOptions}
                                        optionLabel="name"
                                        optionValue="id"
                                        onChange={(e) => updateRow(index, "day", e.value)}
                                        disabled={!row.selected}
                                        style={{ width: "100%", fontSize: "0.85rem" }}
                                        panelStyle={{ fontSize: "0.85rem" }}
                                    />
                                </div>

                                {/* Hour */}
                                <div onClick={(e) => e.stopPropagation()}>
                                    <Dropdown
                                        value={row.hour}
                                        options={hourOptions}
                                        optionLabel="name"
                                        optionValue="id"
                                        onChange={(e) => updateRow(index, "hour", e.value)}
                                        disabled={!row.selected}
                                        style={{ width: "100%", fontSize: "0.85rem" }}
                                        panelStyle={{ fontSize: "0.85rem" }}
                                    />
                                </div>

                                {/* Minute */}
                                <div onClick={(e) => e.stopPropagation()}>
                                    <Dropdown
                                        value={row.minute}
                                        options={minuteOptions}
                                        optionLabel="name"
                                        optionValue="id"
                                        onChange={(e) => updateRow(index, "minute", e.value)}
                                        disabled={!row.selected}
                                        style={{ width: "100%", fontSize: "0.85rem" }}
                                        panelStyle={{ fontSize: "0.85rem" }}
                                    />
                                </div>

                                {/* End time */}
                                <span
                                    style={{
                                        fontSize: "0.78rem",
                                        color: row.selected ? "#2563eb" : "#9ca3af",
                                        fontWeight: row.selected ? 500 : 400,
                                        userSelect: "none",
                                    }}
                                >
                                    {row.selected ? getEndTime(row) : "—"}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Footer summary + actions */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingTop: "0.85rem",
                        }}
                    >
                        <small style={{ color: "#6c757d" }}>
                            {selectedRows.length > 0
                                ? `${selectedRows.length} activit${selectedRows.length > 1 ? "ies" : "y"} selected`
                                : "No activities selected"}
                        </small>
                        <div style={{ display: "flex", gap: "0.75rem" }}>
                            <Button
                                style={{ width: "100px" }}
                                loading={submitting}
                                disabled={selectedRows.length === 0}
                                label="SAVE"
                                type="button"
                                className="p-button-rounded"
                                onClick={saveToDB}
                            />
                            <Button
                                style={{ width: "100px" }}
                                label="CLOSE"
                                onClick={onHide}
                                type="button"
                                className="p-button-rounded p-button-outlined surface-200"
                            />
                        </div>
                    </div>
                </>
            )}
        </Dialog>
    );
};
